import { oc } from "@orpc/contract";
import { z } from "zod";
import { InvoiceLineItemDetailSchema } from "./ar-invoices.contract";
import { EmailImportanceSchema } from "../email-drafts.contract";
import { sendFromOverrideSchema } from "./ar-email-shared.contract";

export const arInvoiceEmailInputSchema = z.object({
  invoiceId: z.string().uuid(),
  recipientEmails: z.array(z.string().email()).min(1),
  /** Line items to drop from the rendered PDF, recomputing its total. */
  excludeLineItemIds: z.array(z.string().uuid()).optional(),
  subjectOverride: z.string().min(1).optional(),
  htmlContentOverride: z.string().min(1).optional(),
  /** Per-send override for the PDF attachment filename. Sanitized server-side. */
  pdfFilenameOverride: z.string().min(1).max(255).optional(),
  /**
   * Telemetry only: did the user actually edit the email subject or body before
   * sending? The client computes this by diffing the draft against the
   * platform-generated baseline. See the same field on the statement email
   * contract for why the server can't infer this from override presence.
   */
  editedBody: z.boolean().optional(),
  /**
   * Per-send routing for the sending mailbox. Omit to use the AR module
   * config (`configured` mode), which falls back to SendGrid when no AR
   * outbox is configured.
   */
  sendFrom: sendFromOverrideSchema.optional(),
  /** When true, BCC the sending user so they receive a copy of the invoice. */
  bccSender: z.boolean().optional(),
  /** Additional BCC addresses (contacts or ad-hoc) beyond the sender. */
  bccRecipientEmails: z.array(z.string().email()).optional(),
  /** Mark the outgoing invoice email's priority. Defaults to "normal". */
  importance: EmailImportanceSchema.optional(),
});

export const arInvoiceEmailPreviewOutputSchema = z.object({
  from: z.string(),
  to: z.string(),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  subject: z.string(),
  htmlContent: z.string(),
  pdfBase64: z.string(),
  pdfFilename: z.string(),
  lineItems: z.array(InvoiceLineItemDetailSchema),
});

export const arInvoiceEmailContracts = {
  preview: oc
    .input(arInvoiceEmailInputSchema)
    .output(arInvoiceEmailPreviewOutputSchema)
    .route({
      method: "POST",
      summary: "Preview an invoice email",
      description:
        "Renders the invoice email without sending: sender, recipients, subject, body, and the invoice PDF, honoring excluded line items and subject, body, or filename overrides.",
    }),

  send: oc
    .input(arInvoiceEmailInputSchema)
    .output(z.object({ outboundEmailId: z.string().uuid() }))
    .route({
      method: "POST",
      summary: "Email an invoice to a customer",
      description:
        "Sends the invoice PDF to the given recipients from the chosen mailbox, with optional BCC and overrides, and records a sent event on the invoice.",
    }),
} as const;
